/**
 * Static frame shells for the Welcome Project export mockup.
 *
 * Uses real production components:
 *   - FrameHeader / FrameFooter (exact props)
 *   - AgentAvatarPreview (DiceBear "glass" style)
 *   - STATUS_DOT_COLORS (canonical kanban colors)
 *   - denker-glass-frame CSS class (no duplicate border/radius/shadow)
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { AgentAvatarPreview } from "@/components/ui/agent-avatar-preview";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";
import { STATUS_DOT_COLORS } from "@/components/shapes/taskboard-frame/task-metadata-chips";

/* Workshop mock labels (the app reads them from its catalogue). */
const STATUS_LABELS: Record<keyof typeof STATUS_DOT_COLORS, string> = {
  backlog: "Backlog",
  todo: "Todo",
  in_progress: "In progress",
  blocked: "Blocked",
  in_review: "In review",
  done: "Done",
};

/* ── FrameShell ─────────────────────────────────────────────────────────
 * Matches production outer container exactly:
 *   surfaceRoleClassName("frame") → "denker-glass-frame"
 *   flex h-full flex-col overflow-hidden
 * All border / radius / shadow / backdrop come from the CSS class.
 * ──────────────────────────────────────────────────────────────────────*/

export function FrameShell({
  width,
  height,
  children,
}: {
  width: number;
  height?: number;
  children: ReactNode;
}) {
  return (
    <div
      className="denker-glass-frame flex flex-col overflow-hidden"
      style={{ width, ...(height ? { height } : {}) }}
    >
      {children}
    </div>
  );
}

/* ── Taskboard (840 × 640) ──────────────────────────────────────────────
 * Columns: exact production classes (rounded-control, bg-surface/30, etc.)
 * Status dots use STATUS_DOT_COLORS color values.
 * Cards use priority left-border + ticket number.
 * ──────────────────────────────────────────────────────────────────────*/

type KanbanStatus =
  | "backlog"
  | "todo"
  | "in_progress"
  | "blocked"
  | "in_review"
  | "done";

const KANBAN_DATA: Array<{
  status: KanbanStatus;
  tasks: Array<{
    title: string;
    ticket: string;
    priority: "urgent" | "high" | "medium" | "low";
  }>;
}> = [
  {
    status: "backlog",
    tasks: [
      { title: "Research competitors", ticket: "Z-4", priority: "low" },
      { title: "Draft go-to-market plan", ticket: "Z-5", priority: "medium" },
    ],
  },
  {
    status: "todo",
    tasks: [
      { title: "Set up repo & CI", ticket: "Z-2", priority: "high" },
      { title: "Design DB schema", ticket: "Z-6", priority: "medium" },
    ],
  },
  {
    status: "in_progress",
    tasks: [
      { title: "Auth module", ticket: "Z-1", priority: "high" },
      { title: "Canvas layout", ticket: "Z-3", priority: "medium" },
    ],
  },
  {
    status: "blocked",
    tasks: [{ title: "API integration", ticket: "Z-7", priority: "high" }],
  },
  {
    status: "in_review",
    tasks: [{ title: "Initial designs", ticket: "Z-8", priority: "low" }],
  },
  {
    status: "done",
    tasks: [
      { title: "Project setup", ticket: "Z-9", priority: "low" },
      { title: "Figma designs", ticket: "Z-10", priority: "low" },
    ],
  },
];

const PRIORITY_LEFT: Record<string, string> = {
  urgent: "border-l-danger",
  high: "border-l-warning",
  medium: "border-l-status-selected",
  low: "border-l-muted",
};

export function TaskboardShell() {
  return (
    <FrameShell width={840} height={640}>
      <FrameHeader
        title="Tasks"
        icon={Icons.Kanban}
        accentColor="bg-accent"
        agentName="Denker"
      />

      {/* Exact production: scrollbar-none nodrag flex min-h-0 flex-1 gap-1.5 overflow-x-auto px-2 py-1 */}
      <div className="scrollbar-none flex min-h-0 flex-1 gap-1.5 overflow-x-auto px-2 py-1">
        {KANBAN_DATA.map(({ status, tasks }) => (
          <div
            key={status}
            className="flex min-w-[120px] flex-1 flex-col rounded-control border border-glass-stroke-faint bg-surface/30"
          >
            {/* Column header: status dot + label + count */}
            <div className="flex shrink-0 items-center gap-1 px-1.5 py-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: STATUS_DOT_COLORS[status] }}
              />
              <span className="min-w-0 flex-1 truncate text-appkit-small font-semibold text-secondary">
                {STATUS_LABELS[status]}
              </span>
              <span className="shrink-0 text-appkit-mini text-muted">
                {tasks.length}
              </span>
            </div>

            {/* Card list */}
            <div className="scrollbar-none min-h-0 flex-1 space-y-1 overflow-y-auto px-1.5 pb-1.5">
              {tasks.map((t) => (
                <div
                  key={t.ticket}
                  className={cn(
                    "rounded-sm border border-l-2 border-glass-stroke-faint bg-white/[0.04] px-1.5 py-1",
                    PRIORITY_LEFT[t.priority] ?? "",
                  )}
                >
                  <p className="text-canvas-sm leading-tight text-secondary">
                    {t.title}
                  </p>
                  <p className="mt-0.5 text-canvas-2xs text-muted/60">
                    {t.ticket}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <FrameFooter />
    </FrameShell>
  );
}

/* ── Agent list (400 × 300) ─────────────────────────────────────────────
 * Uses AgentAvatarPreview (DiceBear "glass" style) + exact row classes.
 * ──────────────────────────────────────────────────────────────────────*/

const AGENTS_DATA = [
  { name: "Denker", seed: "denker-ai", model: "Claude Sonnet" },
  { name: "Designer", seed: "ux-designer", model: "Claude Sonnet" },
  { name: "Marketer", seed: "growth-mk", model: "GPT-4o" },
  { name: "Researcher", seed: "researcher-x", model: "GPT-4o" },
];

export function AgentListShell() {
  return (
    <FrameShell width={400} height={300}>
      <FrameHeader
        title="Team Agents"
        icon={Icons.Users}
        accentColor="bg-accent"
        agentName="Denker"
      />

      {/* Exact production: scrollbar-none nodrag flex flex-1 flex-col gap-0.5 p-2 overflow-y-auto */}
      <div className="scrollbar-none flex flex-1 flex-col gap-0.5 overflow-y-auto p-2">
        {AGENTS_DATA.map((a) => (
          <div
            key={a.name}
            className="flex items-center gap-2 rounded px-1.5 py-1 transition-colors hover:bg-glass-fill-heavy"
          >
            <AgentAvatarPreview styleKey="glass" seed={a.seed} size={24} />
            <div className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-canvas-sm text-secondary">
                {a.name}
              </span>
              <span className="truncate text-canvas-2xs text-muted">
                {a.model}
              </span>
            </div>
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
          </div>
        ))}
      </div>

      <FrameFooter />
    </FrameShell>
  );
}

/* ── Note / checklist frame (400 × 300) ────────────────────────────────
 * Frame type "note" → accent = bg-frame-note (yellow #ffd60a), StickyNote icon.
 * Content = TipTap-style checklist items.
 * ──────────────────────────────────────────────────────────────────────*/

const CHECKLIST = [
  "Watch the intro video →",
  "Ask Denker to create a task for an agent",
  "Watch an agent complete a task",
];

export function ChecklistShell() {
  return (
    <FrameShell width={400} height={300}>
      <FrameHeader
        title="Start here"
        icon={Icons.StickyNote}
        accentColor="bg-frame-note"
        agentName="Denker"
      />

      {/* Note editor body: scrollbar-none nodrag flex-1 overflow-auto px-3 py-2 */}
      <div className="scrollbar-none flex-1 overflow-auto px-3 py-2">
        <p className="mb-2 text-[12px] font-semibold text-primary">
          Start here checklist
        </p>

        {/* TipTap-style task list items */}
        <ul className="space-y-1">
          {CHECKLIST.map((text) => (
            <li key={text} className="flex items-start gap-2">
              <span className="mt-[2px] flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border border-glass-stroke" />
              <span className="text-[11px] leading-snug text-secondary">
                {text}
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-3 text-[10px] text-muted/50">edited just now</p>
      </div>

      <FrameFooter />
    </FrameShell>
  );
}

/* ── Markdown frame (400 × 300) ─────────────────────────────────────── */

export function WelcomeMarkdownShell() {
  return (
    <FrameShell width={400} height={300}>
      <FrameHeader
        title="Welcome to Denker 👋"
        icon={Icons.FileText}
        accentColor="bg-frame-markdown"
        agentName="Denker"
      />

      {/* Exact production: scrollbar-none nodrag relative min-h-0 flex-1 overflow-auto px-3 py-2 */}
      <div className="scrollbar-none relative min-h-0 flex-1 overflow-auto px-3 py-2">
        <h1 className="mb-2 text-[13px] font-bold text-primary">
          Welcome to Denker 👋
        </h1>
        <p className="mb-2 text-[11px] leading-relaxed text-secondary">
          Hi, this is Juan, founder of Denker AI. This is your{" "}
          <strong className="font-semibold text-primary">
            AI co-working space
          </strong>
          .
        </p>
        <p className="mb-2 text-[11px] leading-relaxed text-secondary">
          Everything your agents create appears on this canvas as frames — text,
          code, data, tasks.
        </p>
        <p className="text-[11px] text-muted/70">
          If you have any feedback, DM me on LinkedIn.
        </p>
      </div>

      <FrameFooter />
    </FrameShell>
  );
}

/* ── HTML / video frame (400 × 300) ─────────────────────────────────── */

export function VideoShell() {
  return (
    <FrameShell width={400} height={300}>
      <FrameHeader
        title="How Denker works"
        icon={Icons.Globe}
        accentColor="bg-frame-html"
        agentName="Denker"
      />
      <div className="relative flex-1 overflow-hidden">
        <iframe
          src="https://www.youtube.com/embed/Dps_opdhZi0?rel=0"
          title="Intro to Denker"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full border-0"
        />
      </div>
      <FrameFooter />
    </FrameShell>
  );
}
